import { useTranslation } from "react-i18next";
import {
  Badge,
  BugReportModal,
  useDebug,
  Icon,
  IconButton,
  Popup,
  ProgressBar,
  RecordingWave,
  Reveal,
  SelectList,
  TaskBar,
  type TaskBarProps,
  Text,
  Tooltip,
  usePubSub,
} from "@huub-web/ui";
import { useNavigationState } from "../../providers";
import { type FC, useState } from "react";
import { rem } from "@huub-web/utils";
import { useWebSocket } from "@huub-web/backend";

import { waveStyle } from "./TaskBarController.css";
import { DebugModal } from "../DebugModal/DebugModal";
import { useAppController } from "../../hooks/useAppController";
import { useAppSettings } from "../../hooks/useAppSettings";
import { useTaskBar } from "../../hooks/useTaskBar";

export type TaskBarControllerProps = Pick<TaskBarProps, "variant"> & {
  history?: boolean;
  disabled?: boolean;
  onHistoryClick?: () => void;
};

const GIT_HASH = import.meta.env.VITE_GIT_HASH;

/**
 * Controller for the task bar.
 * Handles audio, suggestions, and microphone permissions.
 */
export const TaskBarController: FC<TaskBarControllerProps> = ({
  variant = "canvas",
  history = false,
  onHistoryClick,
  disabled,
}) => {
  const { t } = useTranslation();
  const pubSub = usePubSub();
  const { isSideContentOpen, navigationContext } = useNavigationState();
  const { isWaitingForAgentResponse } = useWebSocket();
  const { ttsEnabled, isAudioContextRunning, isMessageStreaming } =
    useAppController();
  const { isAudioEnabled, isVisualDebugEnabled, toggleVisualDebug } =
    useAppSettings();
  const { debug } = useDebug();

  const {
    handleMessage,
    handleMessageStop,
    handleRecord,
    handleRecordStop,
    handleSuggestionClick,
    handleAudioToggle,
    isRecording,
    isDisabled,
    microphonePermissionState,
    isProcessingRecording,
    transcription,
    suggestions,
    lastUserPromptType,
    lastUserPrompt,
    volume,
    isLoading,
    hasCanvasTaskBar,
  } = useTaskBar();

  // Popup
  // -------------------

  const [isPopupActive, setIsPopupActive] = useState(false);

  const handlePopupClose = () => {
    setIsPopupActive(false);
  };

  const [isBugReportModalOpen, setIsBugReportModalOpen] = useState(false);
  const [isDebugModalOpen, setIsDebugModalOpen] = useState(false);

  if (variant === "canvas" && !hasCanvasTaskBar) {
    return null;
  }

  return (
    <>
      <TaskBar
        variant={variant}
        immediate={variant === "sideContent"}
        autoFocus
        onMessage={handleMessage}
        onMessageStop={handleMessageStop}
        onRecord={handleRecord}
        onRecordStop={handleRecordStop}
        onSuggestion={handleSuggestionClick}
        prompt={
          lastUserPrompt && !isSideContentOpen ? lastUserPrompt : undefined
        }
        promptType={lastUserPromptType}
        disabled={isDisabled || disabled}
        locked={
          isWaitingForAgentResponse || navigationContext?.[0] === "chatHistory"
        }
        microphoneDisabled={microphonePermissionState === "denied"}
        isRecording={isRecording}
        suggestions={suggestions}
        transcription={
          isRecording || isProcessingRecording ? transcription : ""
        }
        actions={
          <>
            {ttsEnabled && (
              <Tooltip
                value={
                  !isAudioContextRunning && isAudioEnabled
                    ? t("vite.settings.audioPaused")
                    : isAudioEnabled
                      ? t("vite.settings.turnAudioOff")
                      : t("vite.settings.turnAudioOn")
                }
                disabled={isDisabled || disabled}
                placement="top"
              >
                <IconButton
                  icon={isAudioEnabled ? "volumeOn" : "volumeOff"}
                  onClick={handleAudioToggle}
                  size="xxs"
                  rounded
                  border="default"
                  variant="opaque"
                  disabled={isDisabled || disabled}
                >
                  {!isAudioContextRunning && isAudioEnabled && (
                    <Badge variant="warning" value="!" offset="large" />
                  )}
                </IconButton>
              </Tooltip>
            )}

            {history && (
              <Tooltip
                value={t("history.title")}
                disabled={isDisabled || disabled}
                placement="top"
              >
                <IconButton
                  icon="chatHistory"
                  size="xxs"
                  rounded
                  border="default"
                  variant="opaque"
                  onClick={onHistoryClick}
                  disabled={
                    isDisabled ||
                    disabled ||
                    isMessageStreaming ||
                    isWaitingForAgentResponse
                  }
                />
              </Tooltip>
            )}

            {debug && (
              <Popup
                active={isPopupActive}
                onClose={handlePopupClose}
                outsideClose
                content={
                  <SelectList
                    options={[
                      {
                        label: "Refresh templates",
                        value: "refreshTemplates",
                        leading: <Icon icon="reload" size="s" />,
                      },
                      {
                        label: isVisualDebugEnabled
                          ? "Disable visual debugging"
                          : "Enable visual debugging",
                        value: "VisualDebug",
                        leading: (
                          <Icon
                            size="s"
                            icon={
                              <>
                                <path
                                  d="M7.99805 16H11.998M7.99805 11H15.998"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                                <path
                                  d="M7.5 3.5C5.9442 3.54667 5.01661 3.71984 4.37477 4.36227C3.49609 5.24177 3.49609 6.6573 3.49609 9.48836L3.49609 15.9944C3.49609 18.8255 3.49609 20.241 4.37477 21.1205C5.25345 22 6.66767 22 9.49609 22L14.4961 22C17.3245 22 18.7387 22 19.6174 21.1205C20.4961 20.241 20.4961 18.8255 20.4961 15.9944V9.48836C20.4961 6.6573 20.4961 5.24177 19.6174 4.36228C18.9756 3.71984 18.048 3.54667 16.4922 3.5"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                />
                                <path
                                  d="M7.49609 3.75C7.49609 2.7835 8.2796 2 9.24609 2H14.7461C15.7126 2 16.4961 2.7835 16.4961 3.75C16.4961 4.7165 15.7126 5.5 14.7461 5.5H9.24609C8.2796 5.5 7.49609 4.7165 7.49609 3.75Z"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinejoin="round"
                                />
                              </>
                            }
                          />
                        ),
                      },
                      {
                        label: "Debug",
                        value: "debug",
                        leading: (
                          <Text variant="bodyXS" style={{ width: rem(16) }}>
                            {"</>"}
                          </Text>
                        ),
                      },
                      {
                        label: "Bug melden",
                        value: "bugReport",
                        leading: <Icon icon="bug" size="s" />,
                      },
                    ]}
                    onSelect={(option) => {
                      if (option.value === "refreshTemplates") {
                        pubSub.publish("refreshTemplates", {});
                      } else if (option.value === "debug") {
                        setIsDebugModalOpen(true);
                      } else if (option.value === "bugReport") {
                        setIsBugReportModalOpen(true);
                      } else if (option.value === "VisualDebug") {
                        toggleVisualDebug();
                      }
                      setIsPopupActive(false);
                    }}
                  />
                }
                escape
                size="minimal"
                placement={"bottom"}
              >
                <IconButton
                  icon="threeDots"
                  onClick={() => {
                    setIsPopupActive(true);
                  }}
                  size="xxs"
                  rounded
                  border="default"
                  variant="opaque"
                  disabled={isDisabled || disabled}
                  active={isPopupActive}
                />
              </Popup>
            )}

            <Reveal className={waveStyle}>
              {isRecording && (
                <RecordingWave height={28} parts={30} volume={volume} />
              )}
            </Reveal>
          </>
        }
        state={isLoading ? "waiting" : "idle"}
      >
        {isLoading && <ProgressBar variant="infinite" position="absolute" />}
      </TaskBar>

      {isDebugModalOpen && (
        <DebugModal onClose={() => setIsDebugModalOpen(false)} />
      )}

      {isBugReportModalOpen && (
        <BugReportModal
          onClose={() => setIsBugReportModalOpen(false)}
          trailing={
            (__APP_VERSION__ || GIT_HASH) && (
              <Text variant="bodyXS" color="currentColor50">
                {__APP_VERSION__} {(GIT_HASH ?? "local").slice(0, 7)}
              </Text>
            )
          }
        />
      )}
    </>
  );
};
